// src/components/admin/ResetPasswordDialog.jsx
import React, { useState } from 'react';
import { X, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';

const ResetPasswordDialog = ({ employee, onClose, onSubmit }) => {
  const { language } = useApp();
  const t = translations[language];
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // 验证密码长度
    if (newPassword.length < 6) {
      setError(
        language === 'en' ? 'Password must be at least 6 characters' :
        language === 'km' ? 'ពាក្យសម្ងាត់ត្រូវតែមានយ៉ាងហោចណាស់ 6 តួអក្សរ' :
        '密码至少需要6个字符'
      );
      return;
    }

    // 验证密码匹配
    if (newPassword !== confirmPassword) {
      setError(
        language === 'en' ? 'Passwords do not match' :
        language === 'km' ? 'ពាក្យសម្ងាត់មិនត្រូវគ្នា' :
        '两次输入的密码不一致'
      );
      return;
    }

    onSubmit({ new_password: newPassword });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold">
            {language === 'en' ? 'Reset Password' :
             language === 'km' ? 'កំណត់ពាក្យសម្ងាត់ឡើងវិញ' :
             '重置密码'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 员工信息 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">
              {language === 'en' ? 'Employee:' :
               language === 'km' ? 'បុគ្គលិក:' :
               '员工:'}
            </p>
            <p className="font-medium text-gray-900">
              {employee.full_name || employee.username}
            </p>
            <p className="text-sm text-gray-500">
              @{employee.username}
            </p>
          </div>

          {/* 新密码 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'en' ? 'New Password *' :
               language === 'km' ? 'ពាក្យសម្ងាត់ថ្មី *' :
               '新密码 *'}
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={
                  language === 'en' ? 'Enter new password' :
                  language === 'km' ? 'បញ្ចូលពាក្យសម្ងាត់ថ្មី' :
                  '输入新密码'
                }
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showNewPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {language === 'en' ? 'At least 6 characters' :
               language === 'km' ? 'យ៉ាងហោចណាស់ 6 តួអក្សរ' :
               '至少6个字符'}
            </p>
          </div>

          {/* 确认密码 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'en' ? 'Confirm Password *' :
               language === 'km' ? 'បញ្ជាក់ពាក្យសម្ងាត់ *' :
               '确认密码 *'}
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={
                  language === 'en' ? 'Confirm new password' :
                  language === 'km' ? 'បញ្ជាក់ពាក្យសម្ងាត់ថ្មី' :
                  '确认新密码'
                }
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* 错误提示 */}
          {error && (
            <div className="flex items-start space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* 警告信息 */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-orange-700">
                {language === 'en' 
                  ? 'The employee will be logged out and must use the new password to log in.'
                  : language === 'km'
                  ? 'បុគ្គលិកនឹងត្រូវចាកចេញហើយត្រូវប្រើពាក្យសម្ងាត់ថ្មីដើម្បីចូល។'
                  : '员工将被登出,需要使用新密码重新登录。'}
              </p>
            </div>
          </div>

          {/* 按钮 */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              {language === 'en' ? 'Reset Password' :
               language === 'km' ? 'កំណត់ឡើងវិញ' :
               '重置密码'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordDialog;